import { z } from 'zod';

import { REPORT_TYPES, type ReportStatus, type ReportType } from '../domain.js';
import {
  centsSchema,
  dateOnlySchema,
  idSchema,
  optionalText,
  paginationQuerySchema,
} from './common.js';

const reportFields = {
  type: z.enum(REPORT_TYPES),
  title: z.string().trim().min(3, 'Informe um título').max(200),
  description: optionalText(20_000),

  /** Quando a visita ocorreu. Obrigatória em todos os tipos. */
  visitDate: dateOnlySchema,
  /**
   * Quando o ato administrativo ocorreu. Distinta da data da visita —
   * ver projeto_sistema_obras.md §4.4.
   */
  movementDate: dateOnlySchema.optional(),

  /** Valor da movimentação, positivo. A glosa ignora: usa o saldo remanescente. */
  amountCents: centsSchema.optional(),
  /** Dias dos aditivos de prazo, positivo. */
  days: z.number().int().min(1).max(3650).optional(),

  /** Fotos e anexos já enviados via `/midia/upload`. */
  mediaIds: z.array(idSchema).max(60).default([]),

  status: z.enum(['rascunho', 'publicado']).default('publicado'),
};

/** Tipos que exigem valor informado pelo usuário. */
const TYPES_REQUIRING_AMOUNT: readonly ReportType[] = [
  'medicao',
  'aditivo_valor',
  'supressao_valor',
];

/** Tipos que exigem quantidade de dias. */
const TYPES_REQUIRING_DAYS: readonly ReportType[] = ['aditivo_prazo_os', 'aditivo_prazo_contrato'];

interface ReportShape {
  readonly type: ReportType;
  readonly amountCents?: number | undefined;
  readonly days?: number | undefined;
  readonly movementDate?: string | undefined;
}

const amountIsPresentWhenRequired = (value: ReportShape): boolean =>
  !TYPES_REQUIRING_AMOUNT.includes(value.type) ||
  (value.amountCents != null && value.amountCents > 0);

const daysArePresentWhenRequired = (value: ReportShape): boolean =>
  !TYPES_REQUIRING_DAYS.includes(value.type) || (value.days != null && value.days > 0);

/** Visita não tem ato administrativo; os demais tipos precisam da data dele. */
const movementDateIsPresentWhenRequired = (value: ReportShape): boolean =>
  value.type === 'visita' || value.movementDate != null;

export const createReportBodySchema = z
  .object(reportFields)
  .refine(amountIsPresentWhenRequired, {
    message: 'Informe o valor da movimentação',
    path: ['amountCents'],
  })
  .refine(daysArePresentWhenRequired, {
    message: 'Informe a quantidade de dias',
    path: ['days'],
  })
  .refine(movementDateIsPresentWhenRequired, {
    message: 'Informe a data da movimentação',
    path: ['movementDate'],
  });
export type CreateReportBody = z.infer<typeof createReportBodySchema>;

/**
 * Editar relatório.
 *
 * O rascunho se edita livremente. O publicado também, como correção (ADR 060):
 * título, descrição, datas e valor ou dias, com a justificativa obrigatória — o
 * servidor a exige quando o relatório já foi publicado. Mudou o valor, os dias
 * ou a data do ato, o lançamento antigo é estornado e o novo lançado no mesmo
 * ato. O tipo não muda: trocar o tipo é outro relatório. Os anexos do publicado
 * só se acrescentam, por `/anexos`.
 *
 * Campo ausente fica como está; a descrição em `null` é apagada.
 */
export const updateReportBodySchema = z.object({
  title: reportFields.title.optional(),
  description: reportFields.description.nullable(),
  visitDate: dateOnlySchema.optional(),
  movementDate: dateOnlySchema.optional(),
  amountCents: centsSchema.optional(),
  days: z.number().int().min(1).max(3650).optional(),
  mediaIds: z.array(idSchema).max(60).optional(),
  reason: z.string().trim().min(10, 'Explique o motivo da correção').max(1000).optional(),
});
export type UpdateReportBody = z.infer<typeof updateReportBodySchema>;

/** Anexar arquivos a um relatório já publicado — pedido explícito do cliente. */
export const attachMediaBodySchema = z.object({
  mediaIds: z.array(idSchema).min(1).max(60),
});
export type AttachMediaBody = z.infer<typeof attachMediaBodySchema>;

/**
 * Remover relatório exige dizer por quê.
 *
 * O registro continua no histórico, riscado e visível a quem administra — e um
 * relatório riscado sem motivo é pior que nenhum: quem lê meses depois não sabe
 * se aquilo foi engano de digitação ou se o fato nunca aconteceu.
 */
export const deleteReportBodySchema = z.object({
  reason: z.string().trim().min(10, 'Explique o motivo da remoção').max(1000),
});
export type DeleteReportBody = z.infer<typeof deleteReportBodySchema>;

export const listReportsQuerySchema = paginationQuerySchema.extend({
  obraId: idSchema.optional(),
  type: z.enum(REPORT_TYPES).optional(),
  authorId: idSchema.optional(),
  status: z.enum(['rascunho', 'publicado']).optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  /**
   * Traz também os removidos, para quem pode vê-los.
   *
   * Fora por padrão de propósito: o histórico da obra é o que aconteceu, e o
   * que foi removido só interessa a quem está conferindo a remoção. Vem da
   * query string, onde tudo é texto.
   */
  includeDeleted: z.stringbool().optional(),
});
export type ListReportsQuery = z.infer<typeof listReportsQuerySchema>;

// ─────────────────────────────────────────────────────── paralisação

export const startStoppageBodySchema = z.object({
  startedAt: dateOnlySchema,
  reason: z.string().trim().min(3, 'Informe o motivo').max(200),
  description: optionalText(5000),
});
export type StartStoppageBody = z.infer<typeof startStoppageBodySchema>;

export const endStoppageBodySchema = z.object({
  endedAt: dateOnlySchema,
  /** Situação para a qual a obra retorna ao ser retomada. */
  resumeStatus: z.enum(['em_andamento', 'concluida', 'cancelada']).default('em_andamento'),
});
export type EndStoppageBody = z.infer<typeof endStoppageBodySchema>;

// ─────────────────────────────────────────────────────── respostas

export interface ReportAuthor {
  readonly id: string | null;
  readonly name: string;
  readonly avatarUrl: string | null;
}

export interface ReportMedia {
  readonly id: string;
  /**
   * Onde o item é servido.
   *
   * Em foto e documento, o arquivo no CDN. Em vídeo, o **player embutido do
   * Bunny** — as URLs diretas da biblioteca (HLS, MP4, capa) só respondem a
   * requisição com `Referer`, e o player já resolve isso e a adaptação de
   * qualidade de uma vez.
   */
  readonly url: string;
  readonly mime: string;
  readonly kind: 'imagem' | 'video' | 'arquivo';
  /**
   * O nome que o arquivo tinha ao ser enviado.
   *
   * É o que a lista de documentos do relatório mostra, com a extensão. Sem
   * ele cada anexo aparecia como "Documento", e quem lia não tinha como saber
   * se o segundo era a planilha ou o contrato.
   */
  readonly originalName: string;
  readonly sizeBytes: number;
  readonly takenAt: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;

  /** Só em vídeo. */
  readonly thumbnailUrl?: string | undefined;
  readonly durationSeconds?: number | undefined;
  /** `processando` enquanto o Bunny transcodifica; a tela espera em vez de tocar. */
  readonly status?: 'processando' | 'pronto' | 'falhou' | undefined;
}

/** Efeito financeiro/de prazo, para a faixa de delta na timeline. */
export interface ReportMovement {
  readonly id: string;
  readonly type: ReportType;
  readonly amountCents: number;
  readonly days: number | null;
  readonly effectiveDate: string;
  readonly previousValueCents: number;
  readonly newValueCents: number;
  readonly previousPaidCents: number;
  readonly newPaidCents: number;
  readonly previousEndDate: string | null;
  readonly newEndDate: string | null;
  readonly reversalOfId: string | null;
  readonly reversedByMovementId: string | null;
  readonly createdAt: string;
}

/** Preenchido só quando o relatório foi removido. */
export interface ReportDeletion {
  readonly at: string;
  readonly reason: string | null;
  readonly by: ReportAuthor | null;
}

/** A última correção do relatório publicado (ADR 060); as anteriores estão na auditoria. */
export interface ReportEdit {
  readonly at: string;
  readonly reason: string | null;
  readonly by: ReportAuthor | null;
}

export interface ReportSummary {
  readonly id: string;
  readonly obraId: string;
  readonly obraName: string;
  readonly type: ReportType;
  readonly title: string;
  readonly description: string | null;
  readonly visitDate: string;
  readonly movementDate: string | null;
  readonly status: ReportStatus;
  readonly author: ReportAuthor;
  /** Preenchido quando o registro foi feito em substituição ao responsável. */
  readonly onBehalfOf: ReportAuthor | null;
  /**
   * O lançamento que vale. O relatório corrigido tem mais de um no razão — o
   * estornado e o que o substituiu —, e aqui vem o de agora; o estornado só
   * aparece quando não sobrou nenhum (remoção, ou estorno antigo feito à mão).
   */
  readonly movement: ReportMovement | null;
  /**
   * Vazia num relatório removido: os anexos somem com ele, como se apagados, e
   * voltam se for restaurado (ADR 047). `mediaCount` continua contando, para a
   * tarja dizer quantos ficaram ocultos.
   */
  readonly media: readonly ReportMedia[];
  readonly mediaCount: number;
  /** `null` no caso normal; quem não pode ver removidos nunca recebe outro valor. */
  readonly deletion: ReportDeletion | null;
  /** `null` enquanto o relatório não foi corrigido depois de publicado. */
  readonly edit: ReportEdit | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface StoppageEntry {
  readonly id: string;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly reason: string;
  readonly description: string | null;
  readonly createdBy: ReportAuthor | null;
  /** Dias já parados; conta até hoje quando ainda em vigor. */
  readonly durationDays: number;
}

/** Resumo financeiro da obra, com o razão completo. */
export interface ObraLedgerView {
  readonly initialValueCents: number;
  readonly currentValueCents: number;
  readonly paidValueCents: number;
  readonly glossValueCents: number;
  readonly remainingCents: number;
  readonly additionsCents: number;
  readonly suppressionsCents: number;
  readonly movements: readonly (ReportMovement & {
    readonly reportId: string | null;
    readonly reportTitle: string | null;
    readonly authorName: string | null;
  })[];
}
